/**
 * Non-Advisory & Financial Risk Disclaimer.
 *
 * The five numbered sections follow the supplied disclaimer closely. Two deliberate departures:
 * the regulator's own terms are used for its registration categories -- SEBI registers Investment
 * Advisers and Research Analysts, "RIA" being the American term -- and a duplicated word in the
 * liability sentence has been dropped.
 *
 * Section 6 is an addition. The supplied text covers sentiment scores only as something that is
 * not advice; it does not say how they are produced. Given that sentiment is the product's
 * headline feature and the Extremes boards rank companies by it, saying plainly that a model
 * generates them unreviewed is the single most useful sentence on this page.
 */
const disclaimer = {
  title: 'Disclaimer',
  intro: 'Our regulatory status, what our content is and is not, and the risks you accept when '
       + 'acting on anything you read here.',
  versioned: true,
  sections: [
    {
      id: 'educational',
      heading: '1. Educational and Informational Purpose Only',
      blocks: [
        { type: 'p', text:
            'All content, financial news, analysis, stock metrics, charts, data, and tools '
          + 'provided on NiveshFlow (accessible via our website and associated applications) are '
          + 'for **informational and educational purposes only**. None of the information '
          + 'contained on this platform constitutes a solicitation, recommendation, endorsement, '
          + 'or offer by NiveshFlow to buy, sell, or hold any security, equity, mutual fund, or '
          + 'financial instrument.' },
      ],
    },
    {
      id: 'not-advice',
      heading: '2. Not Financial or Investment Advice',
      blocks: [
        { type: 'p', text:
            'NiveshFlow is a software engineering and financial information ecosystem. We are '
          + '**not registered with the Securities and Exchange Board of India (SEBI) as an '
          + 'Investment Adviser (IA) or a Research Analyst (RA)**, nor with any other regulatory '
          + 'financial authority.' },
        { type: 'ul', items: [
          'The insights, sentiment scores, and data models generated by our platform do not '
          + 'constitute personalised financial advice, investment advice, tax advice, or legal '
          + 'advice.',
          'Equity investments are subject to market risks. You must conduct your own independent '
          + 'research, verify all financial metrics, and consult a certified financial planner or '
          + 'qualified investment professional before making any financial decisions.',
        ] },
        { type: 'p', text:
            'No content delivered under a paid plan shall be construed as investment advice. '
          + 'Subscribing does not create an advisory, fiduciary or client relationship between '
          + 'you and NiveshFlow.' },
      ],
    },
    {
      id: 'accuracy',
      heading: '3. Accuracy and Completeness of Data',
      blocks: [
        { type: 'p', text:
            'While we leverage high-performance data ingestion pipelines and dedicated '
          + 'engineering to ensure high availability and data cleanliness, all information on '
          + 'NiveshFlow is provided **“as is” and “as available”** without '
          + 'warranty of any kind.' },
        { type: 'ul', items: [
          'We rely on third-party data feeds, public disclosures, and external APIs for global '
          + 'news and mutual fund tracking.',
          'We do not guarantee the absolute accuracy, completeness, timeliness, or sequencing of '
          + 'the financial information displayed on this website. NiveshFlow shall not be liable '
          + 'for any errors, omissions, delays, or interruptions in data delivery.',
        ] },
        { type: 'p', text:
            'Past performance and historical news sentiment are not indicative of future '
          + 'results.' },
      ],
    },
    {
      id: 'liability',
      heading: '4. Limitation of Liability',
      blocks: [
        { type: 'p', text:
            'Under no circumstances shall NiveshFlow, its founder(s), developers, or affiliates '
          + 'be held liable for any direct, indirect, incidental, consequential, special, or '
          + 'exemplary damages — including but not limited to financial losses, loss of '
          + 'profits, loss of data, or trading losses — arising out of or in connection with '
          + 'your use or inability to use the platform, or your reliance upon any information '
          + 'provided herein. **You use this platform entirely at your own risk.**' },
      ],
    },
    {
      id: 'third-party',
      heading: '5. Third-Party Links and External Content',
      blocks: [
        { type: 'p', text:
            'Our website tracks global financial information and may contain links to external '
          + 'publications, corporate websites, or third-party tools. NiveshFlow does not monitor, '
          + 'endorse, guarantee, or assume responsibility for the accuracy, security, or '
          + 'practices of these external websites. Clicking on any third-party link is done '
          + 'solely at your own discretion.' },
      ],
    },
    {
      id: 'sentiment',
      heading: '6. How Sentiment Scores Are Produced',
      blocks: [
        { type: 'p', text:
            'Sentiment scores on NiveshFlow, including those behind the Extremes boards and any '
          + 'ranking of companies by sentiment, are **generated automatically by a model reading '
          + 'news headlines**. They are not reviewed by a person before publication, are not a '
          + 'judgement about any company’s prospects, and are not a signal to trade.' },
        { type: 'p', text:
            'A model can misread tone, context, sarcasm, or a headline about an unrelated company '
          + 'with a similar name. Treat a score as one imperfect summary of recent coverage, '
          + 'nothing more.' },
        { type: 'p', text:
            'Questions about our content can be sent to the address on our [Contact](/contact) '
          + 'page.' },
      ],
    },
  ],
};

export default disclaimer;
